import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseActionableDialog } from "@/shared/ui/base-actionable-dialog";
import { BaseFileUploader } from "@/shared/ui/base-file-uploader";
import { ExpirableFileOpener } from "@/widgets/expirable-file-opener";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import type React from "react";
import { useState } from "react";

type DeliveryRequestFile = {
  url: string;
  filename: string;
};

type DeliveryRequestFileManagerProps = {
  files: DeliveryRequestFile[];
  onFileUpload: (files: File[]) => void;
  onFileDelete: (fileUrl: string) => void;
  isUploading: boolean;
};

export const DeliveryRequestFileUploader: React.FC<
  DeliveryRequestFileManagerProps
> = ({ files, onFileUpload, onFileDelete, isUploading }) => {
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);

  const handleDeleteClick = (fileUrl: string) => {
    setFileToDelete(fileUrl);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (fileToDelete) {
      onFileDelete(fileToDelete);
      setFileToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const handleDeleteCancel = () => {
    setFileToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const handleUpload = () => {
    if (uploadFiles.length > 0) {
      onFileUpload(uploadFiles);
      setUploadFiles([]);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6" fontWeight={700}>
        配送依頼書
      </Typography>

      {files.length > 0 && (
        <Box>
          <Typography variant="body2" color="grey.600" mb={2}>
            アップロード済みファイル
          </Typography>
          <Stack spacing={1}>
            {files.map((file) => (
              <Box
                key={file.url}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 1,
                }}
              >
                <ExpirableFileOpener url={file.url} filename={file.filename} />
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(file.url)}
                  sx={{ color: "error.main" }}
                >
                  <BaseIcon name="trash" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <Typography variant="body2" color="grey.600" mb={2}>
          {files.length > 0
            ? "追加ファイルをアップロード"
            : "ファイルをアップロード"}
        </Typography>
        <Typography variant="body2" color="grey.500" mb={2}>
          積日の前日16時までにアップロードしてください
        </Typography>
        <Stack spacing={2}>
          <Box maxWidth={376}>
            <BaseFileUploader
              onChange={setUploadFiles}
              options={{
                multiple: false,
              }}
            />
          </Box>
          <BaseButton
            variant="outlined"
            disabled={uploadFiles.length === 0 || isUploading}
            sx={{ maxWidth: 376 }}
            onClick={handleUpload}
          >
            {isUploading ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
              >
                <CircularProgress size={20} />
              </Box>
            ) : (
              "アップロード"
            )}
          </BaseButton>
        </Stack>
      </Box>

      <BaseActionableDialog
        title="ファイルを削除しますか？"
        open={deleteConfirmOpen}
        actionButtonLabel="削除する"
        cancelButtonLabel="キャンセル"
        onClose={handleDeleteCancel}
        onSubmit={handleDeleteConfirm}
      >
        <Typography variant="body2" mb={2}>
          この操作は取り消すことができません。
        </Typography>
      </BaseActionableDialog>
    </Stack>
  );
};
